import { Box, Button, Stack } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import { FC, useState } from 'react';

import { CompanyAssetsQuery } from '../../lib/graphql';

export interface CompanyAssetsViewProps {
  data: {
    company?: CompanyAssetsQuery['company'] | null;
  };
  onAssetUpdate: (file: File | undefined, asset: 'consignee_logo' | 'consignee_background') => Promise<boolean>;
}

const CompanyAsset: FC<{
  onAssetUpdate: (file: File | undefined) => Promise<boolean>;
  url: string | null | undefined;
}> = ({ onAssetUpdate, url }) => {
  const [asset, setAsset] = useState<File | null>(null);
  const [isUpdatingAsset, setIsUpdatingAsset] = useState<boolean>(false);
  const [isRemovingAsset, setIsRemovingAsset] = useState<boolean>(false);

  // As file input is only uncontrolled, the value of it can not be reset after the upload
  // as such, use a workaround to force React to recreate it by changing its 'key' prop
  const [fileKey, setFileKey] = useState<number>(0);

  return (
    <Stack gap="small" direction="column">
      <form
        onSubmit={async (e) => {
          e.preventDefault();

          setIsUpdatingAsset(true);
          if (asset !== null) {
            if (await onAssetUpdate(asset)) {
              setFileKey((prev) => prev + 1);
              setAsset(null);
            }
          }
          setIsUpdatingAsset(false);
        }}>
        <Stack gap="small">
          <input
            key={fileKey}
            type="file"
            accept=".jpg,.png,.svg,.jpeg,.gif"
            onChange={(event) => {
              if (event.target !== null && event.target.files !== null && event.target.validity.valid) {
                setAsset(event.target.files[0]);
              }
            }}
          />
          <Stack direction="row" gap="small" alignItems="center" flexWrap="wrap">
            <Button
              type="submit"
              kind="primary"
              size="small"
              loading={isUpdatingAsset}
              disabled={isUpdatingAsset || isRemovingAsset || asset === null}>
              Update
            </Button>
            <Button
              type="button"
              kind="danger"
              size="small"
              loading={isRemovingAsset}
              disabled={isUpdatingAsset || isRemovingAsset || url === null}
              onClick={async () => {
                setIsRemovingAsset(true);
                if (await onAssetUpdate(undefined)) {
                  setFileKey((prev) => prev + 1);
                  setAsset(null);
                }
                setIsRemovingAsset(false);
              }}>
              Remove
            </Button>
          </Stack>
        </Stack>
      </form>
      <div>
        {notNil(url) ? (
          <Stack gap="xsmall" direction="column">
            <a href={url} target="_blank" rel="noreferrer">
              View full size
            </a>
            <img
              src={url}
              style={{
                border: '1px solid var(--color-neutral-border-subtle)',
                maxWidth: '150px',
                display: 'block',
                backgroundColor: 'white',
                padding: '4px',
                borderRadius: '4px',
              }}
            />
          </Stack>
        ) : (
          <span>No image</span>
        )}
      </div>
    </Stack>
  );
};

export const CompanyAssetsView: FC<CompanyAssetsViewProps> = ({ data, onAssetUpdate }) => (
  <Stack direction="column" gap="large">
    <h2 style={{ margin: 0 }}>Assets</h2>
    <Stack direction="row" gap="xlarge">
      <Box padding="large" style={{ flex: 1 }}>
        <Stack direction="column" gap="medium">
          <h3 style={{ margin: 0 }}>Consignee background</h3>
          <CompanyAsset
            url={data.company?.assets.consigneeBackground?.url}
            onAssetUpdate={async (file) => {
              return await onAssetUpdate(file, 'consignee_background');
            }}
          />
        </Stack>
      </Box>
      <Box padding="large" style={{ flex: 1 }}>
        <Stack direction="column" gap="medium">
          <h3 style={{ margin: 0 }}>Consignee logo</h3>
          <CompanyAsset
            url={data.company?.assets.consigneeLogo?.url}
            onAssetUpdate={async (file) => {
              return await onAssetUpdate(file, 'consignee_logo');
            }}
          />
        </Stack>
      </Box>
    </Stack>
  </Stack>
);
